import { Button, Stack, Typography, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import { DynamicIcon } from 'src/components';
import {
  useComposerBanners,
  useReportBannerVisible,
} from 'src/pages/Conversations/Chat/Banners/composer-banners-context';
import { ChatInfoBackground } from 'src/pages/Conversations/Chat/ChatInfo/chat-info';
import { getBannerActionSx } from 'src/pages/Conversations/Chat/shared-styles';
import { CorrectionPopup } from './correction-popup';

export const PendingAiSuggestionHeader = ({
  promptName,
  show = false,
  onDismiss,
  onRefine,
  onAccept,
}: {
  promptName: string;
  show: boolean;
  onDismiss: () => void;
  onRefine: (correction: string) => void;
  onAccept: () => void;
}) => {
  const theme = useTheme();
  const { hasBannerBelow } = useComposerBanners();
  const correctionButtonRef = useRef<HTMLButtonElement>(null);
  const [popupOpen, setPopupOpen] = useState(false);

  useReportBannerVisible('ai', show);

  const handleToggleCorrectionPopup = () => {
    setPopupOpen((prev) => !prev);
  };

  const handleSubmitCorrection = (correction: string) => {
    setPopupOpen(false);
    onRefine(correction || '');
  };

  if (!show) return null;

  const tertiary = theme.palette.custom.OV.sys.tertiary;
  const ink = tertiary;

  return (
    <ChatInfoBackground
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      textColor={ink}
      bleed={hasBannerBelow('ai')}
    >
      <Stack
        direction="row"
        alignItems="center"
        color={ink}
        gap={1}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <DynamicIcon
          library="lucide"
          name="sparkles"
          size={15}
          sx={{ justifyContent: 'center', lineHeight: 1 }}
        />
        <Typography
          textOverflow="ellipsis"
          overflow="hidden"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
          }}
        >
          {promptName}
        </Typography>
      </Stack>
      <Stack direction="row" gap={0.5} sx={{ marginLeft: 'auto' }}>
        <Button
          variant="text"
          sx={{ ...getBannerActionSx(theme), color: ink }}
          onClick={() => {
            onDismiss();
            setPopupOpen(false);
          }}
        >
          <DynamicIcon library="lucide" name="x" size={13} /> Dismiss
        </Button>
        <Button
          variant="text"
          onClick={handleToggleCorrectionPopup}
          ref={correctionButtonRef}
          sx={{ ...getBannerActionSx(theme), color: ink }}
        >
          <DynamicIcon library="lucide" name="wand-sparkles" size={13} />
          Refine
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onAccept();
            setPopupOpen(false);
          }}
          sx={{
            ...getBannerActionSx(theme),
            // surface token, not white: dark-theme tertiary is pale blue and would fail contrast
            color: theme.palette.custom.OV.sys.surfaceContainerLowest,
            background: tertiary,
            '&:hover': {
              background: tertiary,
              filter: 'brightness(1.07)',
            },
          }}
        >
          <DynamicIcon library="lucide" name="check" size={13} />
          Accept
        </Button>
      </Stack>

      <CorrectionPopup
        anchorEl={correctionButtonRef.current}
        open={popupOpen}
        onSubmit={handleSubmitCorrection}
        onClose={() => setPopupOpen(false)}
      />
    </ChatInfoBackground>
  );
};
